import { apiClient } from '../client';

/**
 * Shopify Playbooks — client bindings for the /shopify/playbooks endpoints.
 * See backend/src/domain/constants/shopifyPlaybooks.js for the catalog shape.
 */

export type ShopifyPlaybookKey =
  | 'order_confirmed'
  | 'order_placed'
  | 'order_cancelled'
  | 'order_shipped'
  | 'order_out_for_delivery'
  | 'order_delivered'
  | 'abandoned_cart'
  | 'new_customer'
  | 'refund_created';

export type ShopifyPlaybookStatus = 'NOT_CONFIGURED' | 'ACTIVE' | 'PAUSED' | 'DRAFT';

export interface ShopifyPlaybookCatalogEntry {
  key: ShopifyPlaybookKey;
  title: string;
  description: string;
  icon: string;
  triggerType: string;
  triggerNodeType: string;
}

export interface ShopifyPlaybookStatusEntry {
  key: ShopifyPlaybookKey;
  title: string;
  description: string;
  icon: string;
  triggerType: string;
  status: ShopifyPlaybookStatus;
  flowId: string | null;
  templateId: string | null;
  updatedAt: string | null;
}

export interface ShopifyPlaybookDetail {
  key: ShopifyPlaybookKey;
  status: ShopifyPlaybookStatus;
  flowId?: string;
  templateId: string | null;
  /**
   * Map from template placeholder key (`"1"`, `"first_name"`) to the
   * expression/literal that will be substituted at send time.
   */
  variables: Record<string, string>;
}

export const shopifyPlaybooksApi = {
  /** Static catalog — safe to cache aggressively. */
  async getCatalog() {
    return apiClient.get<ShopifyPlaybookCatalogEntry[]>('/shopify/playbooks/catalog');
  },

  /** Per-BA status: which playbooks are enabled, which template they use. */
  async list(businessAccountId: string) {
    return apiClient.get<ShopifyPlaybookStatusEntry[]>('/shopify/playbooks', {
      params: { businessAccountId },
    });
  },

  /**
   * Fetch the full persisted state of a single playbook — template id +
   * the variables map. Returns `{ status: 'NOT_CONFIGURED' }` when no flow
   * exists yet, so callers don't need to guard for 404.
   */
  async getDetail(key: ShopifyPlaybookKey, businessAccountId: string) {
    return apiClient.get<ShopifyPlaybookDetail>(`/shopify/playbooks/${key}`, {
      params: { businessAccountId },
    });
  },

  /** Create a new playbook-owned flow. 409 if one already exists for this key. */
  async enable(
    key: ShopifyPlaybookKey,
    body: {
      templateId: string;
      variables?: Record<string, string>;
      businessAccountId: string;
    }
  ) {
    return apiClient.post(`/shopify/playbooks/${key}/enable`, body);
  },

  /** Swap the template, update variables, and/or toggle status. */
  async update(
    key: ShopifyPlaybookKey,
    body: {
      templateId?: string;
      variables?: Record<string, string>;
      status?: 'ACTIVE' | 'PAUSED';
      businessAccountId: string;
    }
  ) {
    return apiClient.patch(`/shopify/playbooks/${key}`, body);
  },

  /** Delete the playbook flow entirely. */
  async remove(key: ShopifyPlaybookKey, businessAccountId: string) {
    const qs = new URLSearchParams({ businessAccountId }).toString();
    return apiClient.delete(`/shopify/playbooks/${key}?${qs}`);
  },
};
